'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import * as T from 'prop-types';
import React, {Fragment} from 'react';
import {CommentIndicator} from '../../../comments/components/CommentIndicator';
import {Button} from '../../../components/buttons/Button';
import {StatelessStatusSelector} from '../../../core-components/selectors/components/StatelessStatusSelector';
import {getAsString, getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {getMessagesFromProps, I18nComponent} from '../../../core-utilities/react-i18n';
import {isTaskType, TaskType} from '../../../tasks/enums/TaskType';


// Note: The TaskCardModeButton only appears on mobile sized screens since the card mode toggle only applies to that screen size. Not sure how best to place this.
export function BaseProjectViewBannerView(props) {
  const isAssetProject = props.taskType === TaskType.ASSET;
  const project = props.project;
  const description = getDescription(isAssetProject, project);
  const address = getAddress(isAssetProject, project);

  return (
    <div className={classNames("ProjectViewBanner", props.className)}>
      <div className="ProjectViewBanner__header">
        <h1 className="ProjectViewBanner__headerLabel">
          {getAsString(project)}
        </h1>

        <div className="ProjectViewBanner__statusSection">
          <span className="ProjectViewBanner__statusLabel">
            {`${props.statusLabel}:`}
          </span>
          {project ? (
            <StatelessStatusSelector
              className="ProjectViewBanner__status"
              labelClassName="ProjectViewBanner__status__label"
              model={project}
              hideValue={false}
              hideIcon={true}

              status={project.status}
              collection={project.collection}
              validStatuses={project.validStatuses}
            />
          ) : null}
          <CommentIndicator
            className="ProjectViewBanner__chat"
            titleClassName="ProjectViewBanner__chatTitle"
            displayTitle={true}
            title={props.chatTitle}
            userId={props.userId}
            chatId={getId(project)}
            collection={getCollection(project)}
            allowNav={false}

            init={props.init}
            {...getMessagesFromProps(props, CommentIndicator.messageProps)}
          />
          {isAssetProject ? null : (
            <Button
              className="ProjectViewBanner__editButton"
              label={props.editLabel}
              onClick={props.onClickEdit}
            />
          )}
        </div>

        {description && (
          <Fragment>
            <span className="LabeledBlock__label">
              {isAssetProject ? props.assetIdLabel : props.descriptionLabel}
            </span>
            <span className={classNames("ProjectViewBanner__description", {
              ProjectViewBanner__description_collapsed: !props.isHeaderExpanded,
              ProjectViewBanner__description_expanded: props.isHeaderExpanded,
            })}
            >
              {description}
              {address && (
                <span className="LabeledBlock__label">
                  {props.assetAddressLabel}
                </span>
              )}
              {address && (
                <span className="ProjectViewBanner__description">
                  {address}
                </span>
              )}
            </span>
          </Fragment>
        )}

        {description && (
          <Button
            className="ProjectViewBanner__expandButton"
            label={props.isHeaderExpanded ? props.collapseLabel : props.expandLabel}
            onClick={props.onToggleHeaderExpanded}
          />
        )}
        <Button
          className="ProjectViewBanner__expandMilestonesButton"
          label={props.isVisibilityAllCollapsed ? props.expandMilestonesLabel : props.collapseMilestonesLabel}
          onClick={props.onClickVisibilityToggleAllButton}
        />
      </div>

      <div className="ProjectViewBanner__headerSubPanel">
        <div className="ProjectViewBanner__mobileButtons">
          {props.canAddTask ? (
            <Button
              className="ProjectViewBanner__addTaskButton"
              activeClassName="ProjectViewBanner__addTaskButton_active"
              label={props.addTaskLabel}
              disabled={!props.canAddTask}
              onClick={props.onAddTask}
            />
          ) : null}
          {props.taskType === TaskType.PROJECT && props.canAddMilestone ? (
            <Button
              className="ProjectViewBanner__addMilestoneButton"
              activeClassName="ProjectViewBanner__addMilestoneButton_active"
              label={props.addMilestoneLabel}
              onClick={props.onAddMilestone}
            />
          ) : null}
          <Button
            className="ProjectViewBanner__toggleCardModeButton BaseButton IconButton FilterIconButton"
            iconClass={props.isCardMode ? 'ion-android-list' : 'ion-ios-browsers-outline'}
            title={props.isCardMode ? props.buttonItemLabel : props.buttonCardLabel}
            onClick={props.onToggleCardMode}
          />
        </div>
      </div>
    </div>
  );
}

BaseProjectViewBannerView.propTypes = {
  className: T.string,
  isHeaderExpanded: T.bool,
  isCollapsed: T.bool, // TODO: (isEveryMilestoneCollapsed)
  userId: T.string.isRequired,
  taskType: isTaskType.isRequired,
  project: T.object,
  canAddTask: T.bool.isRequired,
  canAddMilestone: T.bool.isRequired,

  onClickEdit: T.func.isRequired,
  onToggleHeaderExpanded: T.func.isRequired,
  onToggleCardMode: T.func.isRequired,
  onClickVisibilityToggleAllButton: T.func.isRequired,
  onAddTask: T.func.isRequired,
  onAddMilestone: T.func.isRequired,

  addTaskLabel: T.string,
  addMilestoneLabel: T.string,
  statusLabel: T.string,
  descriptionLabel: T.string,
  assetIdLabel: T.string,
  assetAddressLabel: T.string,
  editLabel: T.string,
  collapseLabel: T.string,
  expandLabel: T.string,
  collapseMilestonesLabel: T.string,
  expandMilestonesLabel: T.string,
  buttonItemLabel: T.string,
  buttonCardLabel: T.string,
  chatTitle: T.string,

  init: T.bool,
};

BaseProjectViewBannerView.defaultProps = {
  init: true,
};

export const ProjectViewBannerView = I18nComponent(BaseProjectViewBannerView, [
  {code: 'status.label', prop: 'statusLabel'},
  {code: 'projects.properties.description.label', prop: 'descriptionLabel'},
  {code: 'assets.properties.identifier.label', prop: 'assetIdLabel'},
  {code: 'assets.properties.address.label', prop: 'assetAddressLabel'},
  {code: 'default.button.edit.label', prop: 'editLabel'},
  {code: 'default.button.collapse.label', prop: 'collapseLabel'},
  {code: 'default.button.expand.label', prop: 'expandLabel'},
  {code: 'default.button.item.label', prop: 'buttonItemLabel'},
  {code: 'default.button.card.label', prop: 'buttonCardLabel'},
  {code: 'project.button.collapse.milestones.label', prop: 'collapseMilestonesLabel'},
  {code: 'project.button.expand.milestones.label', prop: 'expandMilestonesLabel'},
  {code: 'projects.views.chat.title', prop: 'chatTitle'},
  ...CommentIndicator.messages,
]);

function getDescription(isAssetProject, project) {
  if (isAssetProject) {
    // Only display the ID if there is also a name, otherwise the ID is already in the asset project name
    return get(project, 'asset.name') ? get(project, 'asset.identifier') : null;
  } else {
    return get(project, 'description');
  }
}

function getAddress(isAssetProject, project) {
  if (isAssetProject) {
    const tempGeo = get(project, 'asset.geo') ? get(project, 'asset.geo') : null;
    let address;
    try {
      address = JSON.parse(tempGeo).properties.address;
    } catch (e) {
      address = null;
    }
    return address;
  } else {
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/js/projects/components/ProjectViewBanner/ProjectViewBannerView.js